import { MenuItem, Order, TableInfo, MenuCategory, Organization, UserOrgMembership, CreateOrganizationRequest, ApiResponse, CafeStatus, StatusMessages, CafeStatusData } from '../types';
import { auth } from '../config/firebase';
import { API_CONFIG, buildApiUrl } from '../config/api';
import { analyticsService } from './analyticsService';
import { 
  DEMO_ORGANIZATION, 
  DEMO_MENU_ITEMS, 
  DEMO_MENU_CATEGORIES, 
  DEMO_TABLES, 
  DEMO_ORDERS,
  isDemoOrg 
} from '../data/demoData';

// Local storage keys for demo data
const DEMO_ORDERS_STORAGE_KEY = 'beanbuzz_demo_orders';
const DEMO_ORDER_COUNTER_KEY = 'beanbuzz_demo_order_counter';

// Local storage helper methods for demo orders
const getDemoOrdersFromStorage = (): Order[] => {
  try {
    const stored = localStorage.getItem(DEMO_ORDERS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    // Initialize with default demo orders if no stored data
    localStorage.setItem(DEMO_ORDERS_STORAGE_KEY, JSON.stringify(DEMO_ORDERS));
    return DEMO_ORDERS;
  } catch (error) {
    console.error('Error reading demo orders from localStorage:', error);
    // Return fallback demo orders if localStorage fails
    return DEMO_ORDERS || [];
  }
};

const saveDemoOrdersToStorage = (orders: Order[]): void => {
  try {
    localStorage.setItem(DEMO_ORDERS_STORAGE_KEY, JSON.stringify(orders));
  } catch (error) {
    console.error('Error saving demo orders to localStorage:', error);
  }
};

const getNextDemoOrderId = (): string => {
  try {
    const counter = parseInt(localStorage.getItem(DEMO_ORDER_COUNTER_KEY) || '3', 10);
    localStorage.setItem(DEMO_ORDER_COUNTER_KEY, (counter + 1).toString());
    return counter.toString();
  } catch (error) {
    console.error('Error getting next demo order ID:', error);
    return Date.now().toString();
  }
};

// Utility function to clear demo orders (for testing)
const clearDemoOrders = (): void => {
  try {
    localStorage.removeItem(DEMO_ORDERS_STORAGE_KEY);
    localStorage.removeItem(DEMO_ORDER_COUNTER_KEY);
  } catch (error) {
    console.error('Error clearing demo orders:', error);
  }
};

class ApiService {
  private requestCounts: Map<string, { count: number; lastReset: number }> = new Map();
  private readonly MAX_REQUESTS_PER_MINUTE = API_CONFIG.MAX_REQUESTS_PER_MINUTE;
  private readonly RESET_INTERVAL = API_CONFIG.RESET_INTERVAL;
  private currentOrganization: Organization | null = null;
  
  // Cache for organization settings to prevent duplicate calls
  private settingsCache: { [key: string]: { data: any; timestamp: number; promise?: Promise<any> } } = {};
  private readonly CACHE_DURATION = 30000; // 30 seconds

  // Get cached settings or fetch from API with deduplication
  private async getCachedSettings(urlName: string): Promise<any> {
    const cacheKey = `settings_${urlName}`;
    const now = Date.now();
    
    // Check if we have a valid cached result
    if (this.settingsCache[cacheKey] && 
        now - this.settingsCache[cacheKey].timestamp < this.CACHE_DURATION) {
      return this.settingsCache[cacheKey].data;
    }
    
    // Check if there's already a pending request for this organization
    if (this.settingsCache[cacheKey]?.promise) {
      return await this.settingsCache[cacheKey].promise!;
    }
    
    // Create new request and cache the promise
    const promise = this.apiCall(`/api/v1/orgs/${urlName}/settings`);
    this.settingsCache[cacheKey] = {
      data: null,
      timestamp: now,
      promise: promise
    };
    
    try {
      const response = await promise;
      if (response.success) {
        this.settingsCache[cacheKey] = {
          data: response.data,
          timestamp: now
        };
        return response.data;
      } else {
        // Remove failed request from cache
        delete this.settingsCache[cacheKey];
        throw new Error(response.error || 'Failed to fetch settings');
      }
    } catch (error) {
      // Remove failed request from cache
      delete this.settingsCache[cacheKey];
      throw error;
    }
  }

  // Clear cache for a specific organization (useful after updates)
  public clearSettingsCache(urlName: string): void {
    const cacheKey = `settings_${urlName}`;
    delete this.settingsCache[cacheKey];
  }

  // Clear all caches for a specific organization (including public caches)
  public clearAllCaches(urlName: string): void {
    this.clearSettingsCache(urlName);
    
    // Clear any other caches that might be affected by settings changes
    // This includes public menu caches, organization caches, etc.
    const publicCacheKey = `public_${urlName}`;
    delete this.settingsCache[publicCacheKey];
  }

  // Force refresh cache for a specific organization (useful for real-time updates)
  public forceRefreshCache(urlName: string): void {
    this.clearAllCaches(urlName);
  }

  private checkRateLimit(endpoint: string): boolean {
    const now = Date.now();
    const key = endpoint;
    const record = this.requestCounts.get(key);

    if (!record || now - record.lastReset > this.RESET_INTERVAL) {
      // Reset counter
      this.requestCounts.set(key, { count: 1, lastReset: now });
      return true;
    }

    if (record.count >= this.MAX_REQUESTS_PER_MINUTE) {
      console.warn(`Client-side rate limit exceeded for ${endpoint}`);
      return false;
    }

    record.count++;
    return true;
  }

  private async getAuthHeader(forceRefresh: boolean = false): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    try {
      const token = await user.getIdToken(forceRefresh);
      return `Bearer ${token}`;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  // Public method to force refresh token and test auth
  async testFirebaseAuth(): Promise<boolean> {
    const header = await this.getAuthHeader(true); // Force refresh
    return !!header;
  }

  // Central API call method - all requests go through here
  private async apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    // Check client-side rate limit
    if (!this.checkRateLimit(endpoint)) {
      return {
        error: 'Rate limit exceeded. Please wait before making more requests.',
        success: false,
      };
    }

    const startTime = performance.now();
    const method = options.method || 'GET';

    try {

      const authHeader = await this.getAuthHeader();

      const fullUrl = buildApiUrl(endpoint);
      
      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add any additional headers from options
      if (options.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (typeof value === 'string') {
            headers[key] = value;
          }
        });
      }
      
      // Always add Authorization header if we have one
      if (authHeader) {
        headers['Authorization'] = authHeader;
      }

      const response = await fetch(fullUrl, {
        ...options,
        headers,
      });

      // Track API call performance
      const duration = performance.now() - startTime;
      const success = response.ok;
      analyticsService.trackApiCall(endpoint, method, success, duration);

      // Handle rate limiting responses from server
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After') || '60';
        console.warn(`Server rate limit hit. Retry after ${retryAfter} seconds`);
        
        let data: any;
        try {
          data = await response.json();
        } catch {
          data = { error: 'Rate limit exceeded', retryAfter: parseInt(retryAfter) };
        }
        
        return {
          error: data?.error || 'Rate limit exceeded',
          success: false,
        };
      }
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        // Handle non-JSON responses (like 403/404 with no body)
        if (!response.ok) {
          const errorMsg = `HTTP ${response.status}: ${response.statusText}`;
          return { 
            error: errorMsg, 
            success: false 
          };
        }
        data = null;
      }

      if (!response.ok) {
        const errorMsg = data?.error || `HTTP ${response.status}`;
        analyticsService.trackError('api_error', errorMsg);
        return { error: errorMsg, success: false };
      }

      return { data, success: true };
    } catch (error) {
      console.error('API call failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Network error or server unavailable';
      analyticsService.trackError('api_error', errorMessage);
      return { error: 'Network error or server unavailable', success: false };
    }
  }

  // Helper method to check if organization is required
  private requiresOrganization(): ApiResponse<never> | null {
    if (!this.currentOrganization) {
      return {
        error: 'No organization selected',
        success: false,
      };
    }
    return null;
  }

  // Helper method to transform backend order to frontend format
  private transformBackendOrderToFrontend(backendOrder: any): Order {
    return {
      ...backendOrder,
      id: backendOrder.id.toString(),
      timestamp: new Date(backendOrder.createdTime).getTime(),
      shopId: this.currentOrganization!.id.toString(),
      total: backendOrder.totalAmount,
      totalAmount: backendOrder.totalAmount
    } as Order;
  }

  // Helper method to transform frontend order to backend format
  private transformFrontendOrderToBackend(frontendOrder: Omit<Order, 'id' | 'timestamp' | 'shopId'>): any {
    return {
      customerName: frontendOrder.customerName,
      totalAmount: frontendOrder.total,
      currency: 'USD',
      status: frontendOrder.status,
      serviceType: frontendOrder.serviceType,
      tableName: frontendOrder.tableName,
      specialRequests: frontendOrder.specialRequests,
      notes: frontendOrder.notes,
      source: frontendOrder.source,
      items: frontendOrder.items.map(item => ({
        menuItemId: item.menuItem.id,
        quantity: item.quantity,
        unitPrice: item.menuItem.price,
        totalPrice: item.menuItem.price * item.quantity,
        specialRequests: item.specialRequests
      }))
    };
  }

  // Organization API methods
  async getUserOrganizations(): Promise<ApiResponse<{
    organizations: Organization[];
    memberships: UserOrgMembership[];
    count: number;
  }>> {
    // Always fetch user's actual organizations from API when authenticated
    // Demo data should only be returned for unauthenticated users or specific demo routes
    return this.apiCall('/api/v1/orgs/system/user');
  }

  async debugAuth(): Promise<ApiResponse<{
    hasAuthentication: boolean;
    isAuthenticated: boolean;
    authType: string;
    principal: string;
    authorities: string[];
  }>> {
    return this.apiCall('/api/v1/orgs/system/debug/auth');
  }

  async checkUserMembership(): Promise<ApiResponse<{
    hasOrganizations: boolean;
    organizationCount: number;
    maxOrganizations: number;
    canCreateMore: boolean;
  }>> {
    // Always check user's actual membership from API when authenticated
    // Demo data should only be returned for unauthenticated users or specific demo routes
    return this.apiCall('/api/v1/orgs/system/check-membership');
  }

  async createOrganization(orgData: CreateOrganizationRequest): Promise<ApiResponse<{
    organization: Organization;
    membership: UserOrgMembership;
    message: string;
  }>> {
    return this.apiCall('/api/v1/orgs', {
      method: 'POST',
      body: JSON.stringify(orgData),
    });
  }

  async getOrganizationByUrlName(urlName: string): Promise<ApiResponse<{
    organization: Organization;
    memberCount: number;
  }>> {

    if (isDemoOrg(urlName)) {
      try {
        return {
          data: {
            organization: DEMO_ORGANIZATION || {
              id: 1,
              name: 'BeanBuzz Demo',
              urlName: 'beanbuzz-demo',
              createdBy: 'demo@beanbuzz.com',
              createdTime: new Date().toISOString(),
              deleted: false
            },
            memberCount: 1
          },
          success: true
        };
      } catch (error) {
        console.error('Error accessing demo organization data:', error);
        return {
          error: 'Demo organization data temporarily unavailable',
          success: false
        };
      }
    }
    return this.apiCall(`/api/v1/orgs/${urlName}`);
  }

  async getPublicOrganization(urlName: string): Promise<ApiResponse<{
    organization: Organization;
  }>> {
    if (isDemoOrg(urlName)) {
      try {
        return {
          data: {
            organization: {
              id: 1,
              name: 'BeanBuzz Demo',
              urlName: 'beanbuzz-demo',
              createdBy: 'demo@beanbuzz.com',
              createdTime: new Date().toISOString(),
              deleted: false,
              cafeStatus: {
                isOpen: true,
                isBusy: false,
                lastUpdated: new Date().toISOString(),
                updatedBy: 'demo@beanbuzz.com'
              },
              statusMessages: {
                closed: "We're currently closed. Please visit us during our business hours.",
                busy: "We're experiencing high volume. Please visit our counter for faster service.",
                open: "Welcome! You can place your order below."
              }
            }
          },
          success: true
        };
      } catch (error) {
        console.error('Error accessing public demo organization data:', error);
        return {
          error: 'Demo organization data temporarily unavailable',
          success: false
        };
      }
    }
    const response = await this.apiCall(`/api/v1/orgs/public/${urlName}`);
    return response as ApiResponse<{ organization: Organization; }>;
  }

  // REMOVED: joinOrganization - users can no longer join organizations themselves
  // Only admins can add users to organizations

  async addMemberToOrganization(urlName: string, email: string, role: string = 'member'): Promise<ApiResponse<{
    organization: Organization;
    membership: UserOrgMembership;
    message: string;
  }>> {
    return this.apiCall(`/api/v1/orgs/${urlName}/members`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async getOrganizationMembers(urlName: string): Promise<ApiResponse<{
    organization: Organization;
    members: UserOrgMembership[];
    count: number;
  }>> {
    return this.apiCall(`/api/v1/orgs/${urlName}/members`);
  }

  async removeMemberFromOrganization(urlName: string, memberEmail: string): Promise<ApiResponse<{
    message: string;
    removedUser: string;
    organization: Organization;
  }>> {
    return this.apiCall(`/api/v1/orgs/${urlName}/members/${encodeURIComponent(memberEmail)}`, {
      method: 'DELETE',
    });
  }

  async checkUrlAvailability(urlName: string): Promise<ApiResponse<{
    available: boolean;
    urlName: string;
  }>> {
    return this.apiCall(`/api/v1/orgs/url-availability/${urlName}`);
  }

  async updateOrganization(urlName: string, updates: { name?: string }): Promise<ApiResponse<{
    organization: Organization;
    message: string;
  }>> {
    const result = await this.apiCall<{
      organization: Organization;
      message: string;
    }>(`/api/v1/orgs/${urlName}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    
    return result;
  }

  async deleteOrganization(urlName: string): Promise<ApiResponse<{
    message: string;
    organizationName: string;
  }>> {
    const result = await this.apiCall<{
      message: string;
      organizationName: string;
    }>(`/api/v1/orgs/${urlName}`, {
      method: 'DELETE',
    });
    return result;
  }

  async getOrganizationSettings(urlName: string): Promise<ApiResponse<{
    settings: string;
    updatedBy: string | null;
    updatedDate: string | null;
  }>> {
    if (isDemoOrg(urlName)) {
      try {
        return {
          data: {
            settings: '{}',
            updatedBy: 'demo@beanbuzz.com',
            updatedDate: new Date().toISOString()
          },
          success: true
        };
      } catch (error) {
        console.error('Error accessing demo organization settings:', error);
        return {
          data: {
            settings: '{}',
            updatedBy: null,
            updatedDate: null
          },
          success: true,
          message: 'Demo settings temporarily unavailable'
        };
      }
    }
    try {
      // Use cached settings to prevent duplicate API calls
      const responseData = await this.getCachedSettings(urlName);
      
      if (responseData) {
        return {
          data: {
            settings: responseData.settings || '{}',
            updatedBy: responseData.updatedBy || null,
            updatedDate: responseData.updatedDate || null
          },
          success: true
        };
      }
      
      return {
        error: 'Failed to get organization settings',
        success: false
      };
    } catch (error: any) {
      return {
        error: error.message || 'Failed to get organization settings',
        success: false
      };
    }
  }

  async updateOrganizationSettings(urlName: string, settings: string): Promise<ApiResponse<{
    settings: string;
    updatedBy: string;
    updatedDate: string;
    message: string;
  }>> {
    const result = await this.apiCall(`/api/v1/orgs/${urlName}/settings`, {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });
    
    // Clear all caches after successful update to ensure fresh data on next request
    // This includes both admin and public caches
    if (result.success) {
      this.clearAllCaches(urlName);
    }
    
    return result as ApiResponse<{
      settings: string;
      updatedBy: string;
      updatedDate: string;
      message: string;
    }>;
  }

  // Cafe Status API methods
  async getCafeStatus(urlName: string): Promise<ApiResponse<CafeStatusData>> {
    if (isDemoOrg(urlName)) {
      // Return demo cafe status data
      return {
        data: {
          cafeStatus: {
            isOpen: true,
            isBusy: false,
            lastUpdated: new Date().toISOString(),
            updatedBy: 'demo@example.com'
          },
          statusMessages: {
            closed: "We're currently closed. Please visit us during our business hours.",
            busy: "We're experiencing high volume. Please visit our counter for faster service.",
            open: "Welcome! You can place your order below."
          }
        },
        success: true
      };
    }
    
    const orgCheck = this.requiresOrganization();
    if (orgCheck) return orgCheck;
    
    try {
      // Use cached settings to prevent duplicate API calls
      const responseData = await this.getCachedSettings(urlName);
      
      if (responseData) {
        // Extract cafe status data from the settings response
        const cafeStatusData: CafeStatusData = {
          cafeStatus: responseData.cafeStatus || {
            isOpen: false,
            isBusy: false
          },
          statusMessages: responseData.statusMessages || {
            closed: "We're currently closed. Please visit us during our business hours.",
            busy: "We're experiencing high volume. Please visit our counter for faster service.",
            open: "Welcome! You can place your order below."
          }
        };
        
        return {
          data: cafeStatusData,
          success: true
        };
      }
      
      return {
        error: 'Failed to get cafe status data',
        success: false
      };
    } catch (error: any) {
      return {
        error: error.message || 'Failed to get cafe status',
        success: false
      };
    }
  }

  async updateCafeStatus(urlName: string, cafeStatus: Partial<CafeStatus>, statusMessages?: Partial<StatusMessages>): Promise<ApiResponse<{
    settings: string;
    updatedBy: string;
    updatedDate: string;
    message: string;
  }>> {
    if (isDemoOrg(urlName)) {
      // Demo mode - just return success
      return {
        data: {
          settings: '{}',
          updatedBy: 'demo@example.com',
          updatedDate: new Date().toISOString(),
          message: 'Demo mode - settings not actually saved'
        },
        success: true
      };
    }
    
    const orgCheck = this.requiresOrganization();
    if (orgCheck) return orgCheck;
    
    // Get current settings first
    const currentSettingsResponse = await this.getOrganizationSettings(urlName);
    if (!currentSettingsResponse.success || !currentSettingsResponse.data) {
      return {
        error: 'Failed to get current settings',
        success: false
      };
    }
    
    let currentSettings: any = {};
    try {
      currentSettings = JSON.parse(currentSettingsResponse.data.settings || '{}');
    } catch (e) {
      currentSettings = {};
    }
    
    // Update cafe status and messages
    const updatedSettings: any = {
      ...currentSettings,
      cafeStatus: {
        ...(currentSettings.cafeStatus || {}),
        ...cafeStatus,
        lastUpdated: new Date().toISOString(),
        updatedBy: this.currentOrganization?.createdBy || 'unknown'
      }
    };
    
    if (statusMessages) {
      updatedSettings.statusMessages = {
        ...(currentSettings.statusMessages || {}),
        ...statusMessages
      };
    }
    
    const result = await this.updateOrganizationSettings(urlName, JSON.stringify(updatedSettings));
    
    // Clear all caches after successful update to ensure fresh data on next request
    // This includes both admin and public caches
    if (result.success) {
      this.clearAllCaches(urlName);
    }
    
    return result;
  }

  // Organization utility methods
  generateUrlName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim();
  }

  isValidUrlName(urlName: string): boolean {
    return /^[a-z0-9-]+$/.test(urlName);
  }

  // Menu API methods
  async getMenuItems(urlName: string): Promise<ApiResponse<{ items: MenuItem[]; count: number }>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      return {
        data: {
          items: DEMO_MENU_ITEMS,
          count: DEMO_MENU_ITEMS.length
        },
        success: true
      };
    }
    return this.apiCall(`/api/v1/menu/items/${urlName}`);
  }

  async getPublicMenu(urlName: string): Promise<ApiResponse<{ items: MenuItem[]; count: number }>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      try {
        return {
          data: {
            items: DEMO_MENU_ITEMS || [],
            count: DEMO_MENU_ITEMS?.length || 0
          },
          success: true
        };
      } catch (error) {
        console.error('Error accessing public demo menu data:', error);
        return {
          data: {
            items: [],
            count: 0
          },
          success: true,
          message: 'Demo data temporarily unavailable'
        };
      }
    }
    return this.apiCall(`/api/v1/menu/public/${urlName}`);
  }

  async getMenuCategories(urlName: string): Promise<ApiResponse<{ categories: MenuCategory[]; count: number }>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      try {
        return {
          data: {
            categories: DEMO_MENU_CATEGORIES || [],
            count: DEMO_MENU_CATEGORIES?.length || 0
          },
          success: true
        };
      } catch (error) {
        console.error('Error accessing demo menu categories data:', error);
        return {
          data: {
            categories: [],
            count: 0
          },
          success: true,
          message: 'Demo categories temporarily unavailable'
        };
      }
    }
    return this.apiCall(`/api/v1/menu/categories/${urlName}`);
  }

  async getPublicCategories(urlName: string): Promise<ApiResponse<{ categories: MenuCategory[]; count: number }>> {
    if (isDemoOrg(urlName)) { // Demo organization
      return {
        data: {
          categories: DEMO_MENU_CATEGORIES.filter(cat => cat.isActive),
          count: DEMO_MENU_CATEGORIES.filter(cat => cat.isActive).length
        },
        success: true
      };
    }
    
    const response = await this.apiCall<any>(`/api/v1/menu/public/categories/${urlName}`);
    if (!response.success) {
      return response;
    }
    
    return {
      ...response,
      data: {
        categories: response.data.categories || [],
        count: response.data.count || 0
      }
    };
  }

  async getPublicActiveTables(urlName: string, tableId?: string): Promise<ApiResponse<{ tables: TableInfo[]; count: number } | { table: TableInfo }>> {
    if (isDemoOrg(urlName)) { // Demo organization
      if (tableId) {
        // Return specific table
        const table = DEMO_TABLES.find(t => t.id === tableId && t.isActive);
        if (!table) {
          return {
            error: 'Table not found or not active',
            success: false
          };
        }
        return {
          data: { table },
          success: true
        };
      } else {
        // Return all active tables
        return {
          data: {
            tables: DEMO_TABLES.filter(table => table.isActive),
            count: DEMO_TABLES.filter(table => table.isActive).length
          },
          success: true
        };
      }
    }
    
    const endpoint = tableId ? `/api/v1/tables/public/${urlName}/table/${tableId}` : `/api/v1/tables/public/${urlName}`;
    const response = await this.apiCall<any>(endpoint);
    if (!response.success) {
      return response;
    }
    
    return {
      ...response,
      data: tableId ? { table: response.data.table } : { tables: response.data.tables || [], count: response.data.count || 0 }
    };
  }

  async createMenuItem(urlName: string, item: Omit<MenuItem, 'id'>): Promise<ApiResponse<{ item: MenuItem; message: string }>> {
    return this.apiCall(`/api/v1/menu/items/${urlName}`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateMenuItem(urlName: string, itemId: number, updates: Partial<MenuItem>): Promise<ApiResponse<{ item: MenuItem; message: string }>> {
    return this.apiCall(`/api/v1/menu/items/${urlName}/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteMenuItem(urlName: string, itemId: number): Promise<ApiResponse<{ message: string }>> {
    return this.apiCall(`/api/v1/menu/items/${urlName}/${itemId}`, {
      method: 'DELETE',
    });
  }

  async createMenuCategory(urlName: string, category: Pick<MenuCategory, 'name' | 'description' | 'displayOrder' | 'isActive'>): Promise<ApiResponse<{ category: MenuCategory; message: string }>> {
    return this.apiCall(`/api/v1/menu/categories/${urlName}`, {
      method: 'POST',
      body: JSON.stringify(category),
    });
  }

  async updateMenuCategory(urlName: string, categoryId: number, updates: Partial<MenuCategory>): Promise<ApiResponse<{ category: MenuCategory; message: string }>> {
    return this.apiCall(`/api/v1/menu/categories/${urlName}/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteMenuCategory(urlName: string, categoryId: number): Promise<ApiResponse<{ message: string }>> {
    return this.apiCall(`/api/v1/menu/categories/${urlName}/${categoryId}`, {
      method: 'DELETE',
    });
  }

  // Orders API methods - now standardized to use ApiResponse
  async getOrders(): Promise<ApiResponse<Order[]>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      try {
        const orders = getDemoOrdersFromStorage();
        return {
          data: orders,
          success: true
        };
      } catch (error) {
        console.error('Error accessing demo orders data:', error);
        return {
          data: [],
          success: true,
          message: 'Demo orders temporarily unavailable'
        };
      }
    }
    
    const orgCheck = this.requiresOrganization();
    if (orgCheck) return orgCheck;
    
    const response = await this.apiCall<any[]>(`/api/v1/orgs/${this.currentOrganization!.urlName}/orders`);
    if (!response.success) {
      return response;
    }
    
    const transformedOrders = response.data?.map(order => this.transformBackendOrderToFrontend(order)) || [];
    return {
      ...response,
      data: transformedOrders
    };
  }

  async getActiveOrders(): Promise<ApiResponse<Order[]>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      try {
        const orders = getDemoOrdersFromStorage();
        const activeOrders = orders.filter(order => order.status !== 'completed');
        return {
          data: activeOrders,
          success: true
        };
      } catch (error) {
        console.error('Error accessing demo active orders data:', error);
        return {
          data: [],
          success: true,
          message: 'Demo active orders temporarily unavailable'
        };
      }
    }
    
    const orgCheck = this.requiresOrganization();
    if (orgCheck) return orgCheck;
    
    const response = await this.apiCall<any[]>(`/api/v1/orgs/${this.currentOrganization!.urlName}/orders/active`);
    if (!response.success) {
      return response;
    }
    
    const transformedOrders = response.data?.map(order => this.transformBackendOrderToFrontend(order)) || [];
    return {
      ...response,
      data: transformedOrders
    };
  }

  async createOrder(order: Omit<Order, 'id' | 'timestamp' | 'shopId'>): Promise<ApiResponse<Order>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      const newOrder: Order = {
        ...order,
        id: getNextDemoOrderId(),
        timestamp: Date.now(),
        shopId: '1'
      };
      
      const currentOrders = getDemoOrdersFromStorage();
      const updatedOrders = [...currentOrders, newOrder];
      saveDemoOrdersToStorage(updatedOrders);
      
      return {
        data: newOrder,
        success: true
      };
    }
    
    const orgCheck = this.requiresOrganization();
    if (orgCheck) return orgCheck;
    
    const backendOrder = this.transformFrontendOrderToBackend(order);
    const response = await this.apiCall<any>(`/api/v1/orgs/${this.currentOrganization!.urlName}/orders`, {
      method: 'POST',
      body: JSON.stringify(backendOrder)
    });
    
    if (!response.success) {
      return response;
    }
    
    const transformedOrder = this.transformBackendOrderToFrontend(response.data);
    return {
      ...response,
      data: transformedOrder
    };
  }

  async updateOrderStatus(id: string, status: Order['status']): Promise<ApiResponse<Order>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      try {
        const orders = getDemoOrdersFromStorage();
        const orderIndex = orders.findIndex(order => order.id === id);
        
        if (orderIndex === -1) {
          return {
            error: 'Order not found',
            success: false
          };
        }
        
        const updatedOrder = { ...orders[orderIndex], status };
        orders[orderIndex] = updatedOrder;
        saveDemoOrdersToStorage(orders);
        
        return {
          data: updatedOrder,
          success: true
        };
      } catch (error) {
        console.error('Error updating demo order status:', error);
        return {
          error: 'Failed to update demo order status',
          success: false
        };
      }
    }
    
    const orgCheck = this.requiresOrganization();
    if (orgCheck) return orgCheck;
    
    const response = await this.apiCall<any>(`/api/v1/orgs/${this.currentOrganization!.urlName}/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
    
    if (!response.success) {
      return response;
    }
    
    // Get the updated order
    return this.getOrderById(id);
  }

  async getOrderById(id: string): Promise<ApiResponse<Order>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      try {
        const orders = getDemoOrdersFromStorage();
        const order = orders.find(order => order.id === id);
        
        if (!order) {
          return {
            error: 'Order not found',
            success: false
          };
        }
        
        return {
          data: order,
          success: true
        };
      } catch (error) {
        console.error('Error getting demo order by ID:', error);
        return {
          error: 'Failed to get demo order',
          success: false
        };
      }
    }
    
    const orgCheck = this.requiresOrganization();
    if (orgCheck) return orgCheck;
    
    const response = await this.apiCall<any>(`/api/v1/orgs/${this.currentOrganization!.urlName}/orders/${id}`);
    if (!response.success) {
      return response;
    }
    
    const transformedOrder = this.transformBackendOrderToFrontend(response.data);
    return {
      ...response,
      data: transformedOrder
    };
  }

  async deleteOrder(id: string): Promise<ApiResponse<void>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      try {
        const orders = getDemoOrdersFromStorage();
        const filteredOrders = orders.filter(order => order.id !== id);
        
        if (filteredOrders.length === orders.length) {
          return {
            error: 'Order not found',
            success: false
          };
        }
        
        saveDemoOrdersToStorage(filteredOrders);
        
        return {
          success: true
        };
      } catch (error) {
        console.error('Error deleting demo order:', error);
        return {
          error: 'Failed to delete demo order',
          success: false
        };
      }
    }
    
    const orgCheck = this.requiresOrganization();
    if (orgCheck) return orgCheck;
    
    return this.apiCall(`/api/v1/orgs/${this.currentOrganization!.urlName}/orders/${id}`, {
      method: 'DELETE'
    });
  }

  async getOrderLimits(): Promise<ApiResponse<{
    ordersInLast24Hours: number;
    maxOrdersPerDay: number;
    canCreateMore: boolean;
    remainingOrders: number;
  }>> {
    const orgCheck = this.requiresOrganization();
    if (orgCheck) return orgCheck;
    
    return this.apiCall(`/api/v1/orgs/${this.currentOrganization!.urlName}/orders/limits`);
  }

  async createPublicOrder(orgUrl: string, tableId: string, order: Omit<Order, 'id' | 'timestamp' | 'shopId'>): Promise<ApiResponse<{
    orderId: string;
    orderNumber: string;
    orgUrl: string;
    tableId: string;
    message: string;
    estimatedTime: number;
  }>> {
    if (isDemoOrg(orgUrl)) { // Demo organization
      const newOrder: Order = {
        ...order,
        id: getNextDemoOrderId(),
        timestamp: Date.now(),
        shopId: '1'
      };
      
      const currentOrders = getDemoOrdersFromStorage();
      const updatedOrders = [...currentOrders, newOrder];
      saveDemoOrdersToStorage(updatedOrders);
      
      return {
        data: {
          orderId: newOrder.id,
          orderNumber: newOrder.id,
          orgUrl,
          tableId,
          message: 'Order created successfully',
          estimatedTime: 15
        },
        success: true
      };
    }
    
    const response = await this.apiCall<any>(`/api/v1/orgs/${orgUrl}/orders/public/${orgUrl}/${tableId}`, {
      method: 'POST',
      body: JSON.stringify(this.transformFrontendOrderToBackend(order))
    });
    
    if (!response.success) {
      return response;
    }
    
    return {
      ...response,
      data: {
        orderId: response.data.orderId,
        orderNumber: response.data.orderNumber,
        orgUrl: response.data.orgUrl,
        tableId: response.data.tableId,
        message: response.data.message,
        estimatedTime: response.data.estimatedTime
      }
    };
  }

  async getPublicOrderStatus(orgUrl: string, orderId: string): Promise<ApiResponse<{
    orderId: string;
    orderNumber: string;
    status: string;
    customerName: string;
    total: number;
    timestamp: string;
    tableName: string;
    items: any[];
    notes: string;
  }>> {
    if (isDemoOrg(orgUrl)) { // Demo organization
      const currentOrders = getDemoOrdersFromStorage();
      const order = currentOrders.find(o => o.id === orderId);
      
      if (!order) {
        return {
          success: false,
          error: 'Order not found'
        };
      }
      
      return {
        data: {
          orderId: order.id,
          orderNumber: order.id,
          status: order.status,
          customerName: order.customerName,
          total: order.total,
          timestamp: new Date(order.timestamp).toISOString(),
          tableName: order.tableName || 'Unknown',
          items: order.items || [],
          notes: order.notes || ''
        },
        success: true
      };
    }
    
    const response = await this.apiCall<any>(`/api/v1/orgs/${orgUrl}/orders/public/${orgUrl}/${orderId}`, {
      method: 'GET'
    });
    
    if (!response.success) {
      return response;
    }
    
    return {
      ...response,
      data: {
        orderId: response.data.orderId,
        orderNumber: response.data.orderNumber,
        status: response.data.status,
        customerName: response.data.customerName,
        total: response.data.total,
        timestamp: response.data.timestamp,
        tableName: response.data.tableName,
        items: response.data.items,
        notes: response.data.notes
      }
    };
  }

  // Tables API methods
  async getTables(urlName: string): Promise<ApiResponse<{ tables: TableInfo[]; count: number }>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      try {
        return {
          data: {
            tables: DEMO_TABLES || [],
            count: DEMO_TABLES?.length || 0
          },
          success: true
        };
      } catch (error) {
        console.error('Error accessing demo tables data:', error);
        return {
          data: {
            tables: [],
            count: 0
          },
          success: true,
          message: 'Demo tables temporarily unavailable'
        };
      }
    }
    return this.apiCall(`/api/v1/tables/${urlName}`);
  }

  async getActiveTables(urlName: string): Promise<ApiResponse<{ tables: TableInfo[]; count: number }>> {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) { // Demo organization
      try {
        const activeTables = DEMO_TABLES?.filter(table => table.isActive) || [];
        return {
          data: {
            tables: activeTables,
            count: activeTables.length
          },
          success: true
        };
      } catch (error) {
        console.error('Error accessing demo active tables data:', error);
        return {
          data: {
            tables: [],
            count: 0
          },
          success: true,
          message: 'Demo active tables temporarily unavailable'
        };
      }
    }
    return this.apiCall(`/api/v1/tables/${urlName}/active`);
  }

  async createTable(urlName: string, table: Pick<TableInfo, 'name' | 'description' | 'displayOrder' | 'isActive'>): Promise<ApiResponse<{ table: TableInfo; message: string }>> {
    return this.apiCall(`/api/v1/tables/${urlName}`, {
      method: 'POST',
      body: JSON.stringify(table),
    });
  }

  async updateTable(urlName: string, tableId: string, updates: Partial<TableInfo>): Promise<ApiResponse<{ table: TableInfo; message: string }>> {
    return this.apiCall(`/api/v1/tables/${urlName}/${tableId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteTable(urlName: string, tableId: string): Promise<ApiResponse<{ message: string }>> {
    return this.apiCall(`/api/v1/tables/${urlName}/${tableId}`, {
      method: 'DELETE',
    });
  }

  // Organization management
  setCurrentOrganization(org: Organization | null): void {
    this.currentOrganization = org;
  }

  getCurrentOrganization(): Organization | null {
    return this.currentOrganization;
  }

  // Utility methods
  async clearAllData(): Promise<ApiResponse<void>> {
    // TODO: Implement if needed for admin functions
    return {
      error: 'Clear all data API not implemented yet',
      success: false
    };
  }

  // Demo-specific utility methods
  clearDemoOrders(): void {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) {
      try {
        clearDemoOrders();
      } catch (error) {
        console.error('Error clearing demo orders:', error);
      }
    }
  }

  getDemoOrdersCount(): number {
    if (isDemoOrg(this.currentOrganization?.urlName || '')) {
      try {
        return getDemoOrdersFromStorage().length;
      } catch (error) {
        console.error('Error getting demo orders count:', error);
        return 0;
      }
    }
    return 0;
  }
}

// Export singleton instance
export const apiService = new ApiService(); 